import React, { useState } from 'react';
import { Icons } from '@/assets';
import { Icon } from '@/components/Icon';
import { SaveApiResMessage } from '@/actions';
import IconButtonV2 from '@/ctComponents/IconButton/IconButtonV2';

const FileUpload = ({
  title = 'Drag & Drop your files',
  acceptedFileTypes = '.csv,.xlsx,.xls',
  fileTypeDescription = 'Only support .XlSX, .cvs',
  maxFileSize = 5000000, // 5MB in bytes
  maxFiles = 1,
  onFilesChange,
  uploadedFiles = [],
  onRemoveFile,
  className = '',
  uploadIcon = Icons.imageUpload,
  iconWidth = 214,
  iconHeight = 133,
  dragIndicatorText = 'Drop here',
  browseButtonText = 'Browse files',
  allowMultiple = false,
  customValidation = null,
  disabled = false,
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [draggedFileName, setDraggedFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(0)} KB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
    }
  };

  const getFileIcon = () => {
    // You can add more specific icons based on file type
    return Icons.videoPlayFile;
  };

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxFileSize) {
      SaveApiResMessage({
        title: 'Warning',
        message: `File size must be less than ${(
          maxFileSize /
          (1024 * 1024)
        ).toFixed(0)}MB.`,
        mode: 'warning',
      });
      return false;
    }

    // Check file type
    const allowedTypes = acceptedFileTypes?.split(',') || [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/csv',
      'text/plain',
    ];

    const fileExtension = file.name.toLowerCase().split('.').pop();
    console.log({ allowedTypes, fileExtension }, file.type);
    const isValidType =
      allowedTypes.includes(`.${fileExtension}`) ||
      ['csv', 'xlsx', 'xls'].includes(fileExtension);

    if (!isValidType) {
      SaveApiResMessage({
        title: 'Warning',
        message: `You can only upload ${acceptedFileTypes} files.`,
        mode: 'warning',
      });
      return false;
    }

    // Custom validation if provided
    if (customValidation && !customValidation(file)) {
      return false;
    }

    return true;
  };

  const processFiles = (files) => {
    if (!files.length) return;

    const validFiles = Array.from(files).filter(validateFile);

    if (validFiles.length + uploadedFiles.length > maxFiles) {
      SaveApiResMessage({
        title: 'Warning',
        message: `You can upload only ${maxFiles} file${
          maxFiles > 1 ? 's' : ''
        } at a time.`,
        mode: 'warning',
      });
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    // Check for duplicates
    const filteredFiles = newFiles.filter(
      (newFile) =>
        !uploadedFiles.some(
          (existingFile) => existingFile.name === newFile.name
        )
    );

    //    if (filteredFiles.length > 0) {
    onFilesChange(filteredFiles);
    // } else {
    //   SaveApiResMessage({
    //     title: 'Warning',
    //     message: 'File with this name was already selected.',
    //     mode: 'warning',
    //   });
    // }

    if (validFiles.length > 0) {
      setDraggedFileName(
        validFiles.length === 1 ? validFiles[0].name : dragIndicatorText
      );
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragCounter((prev) => prev + 1);
    setIsDragging(true);
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    // Check if we're actually leaving the drop zone (not just moving to a child element)
    // On Mac, dragleave fires when moving from parent to child, so we need to verify
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    // Only reset if we're actually outside the drop zone boundaries
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragCounter((prev) => {
        const newCount = prev - 1;
        if (newCount === 0) {
          setIsDragging(false);
          setDraggedFileName(dragIndicatorText);
        }
        return newCount;
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragging(false);
    setDragCounter(0);
    processFiles(e.dataTransfer.files);
  };

  const handleMouseMove = (e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleAddFile = (event) => {
    if (disabled) return;
    processFiles(event.target.files);
    // Reset the input value so the same file can be selected again
    event.target.value = '';
  };

  const handleRemoveFile = (index) => {
    if (disabled) return;
    if (onRemoveFile) {
      onRemoveFile(index);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="w-full">
        {/* Drag and Drop Upload Area */}
        <div
          className={`border-2 border-dashed border-border-secondary rounded-2xl p-4 transition-colors ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            uploadedFiles.length > 0
              ? 'bg-gray-50 border-solid'
              : 'flex flex-col items-center justify-center text-center'
          }`}
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onMouseMove={handleMouseMove}
          style={{
            backgroundColor: isDragging ? '#FEF7E8' : '#fff',
            borderColor: isDragging ? '#FFAD00' : '#d1d5db',
            borderStyle: uploadedFiles.length > 0 ? 'solid' : 'dashed',
            transition: 'all 0.2s ease',
          }}>
          {uploadedFiles.length > 0 ? (
            // Show uploaded files inside the area
            <div className="w-full">
              {uploadedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between w-full ">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div
                      className="flex-shrink-0 rounded-lg text-primary-blue-dark w-12 h-12 flex items-center justify-center"
                      style={{
                        backgroundColor: 'rgba(40, 126, 255, 0.10)',
                      }}>
                      <Icon src={getFileIcon()} className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-primary-text truncate m-0">
                        {file.name}
                      </p>
                      <p className="text-xs text-inactive-bg m-0 mt-1">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    <IconButtonV2
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      noBorder
                      disabled={disabled}
                      icon="xCircle"
                      isGray={false}
                    />
                  </div>
                </div>
              ))}
              {/* Allow uploading more files */}
              {/* <div className="mt-3 pt-3 border-t border-gray-200">
                <label
                  htmlFor="file-input-upload"
                  className={`inline-flex items-center px-4 py-2 rounded text-sm text-yellow-light font-medium border border-yellow-light transition-all duration-200 ${
                    disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-yellow-light hover:text-white cursor-pointer'
                  }`}>
                  {browseButtonText}
                </label>
                <input
                  id="file-input-upload"
                  type="file"
                  accept={acceptedFileTypes}
                  multiple={allowMultiple}
                  style={{ display: 'none' }}
                  onChange={handleAddFile}
                  disabled={disabled}
                />
              </div> */}
            </div>
          ) : (
            // Default upload UI
            <>
              <Icon src={uploadIcon} width={iconWidth} height={iconHeight} />
              <div className="space-y-2">
                <p className="text-lg font-bold text-primary-text m-0">
                  {title}
                </p>
                <div className="flex items-center justify-center space-x-2">
                  <div className="h-px bg-border-tertiary flex-1"></div>
                  <span className="text-xs text-icon-gray">OR</span>
                  <div className="h-px bg-border-tertiary flex-1"></div>
                </div>
                <div>
                  <label
                    htmlFor="file-input-upload"
                    className={`inline-flex items-center px-6 rounded py-2 border border-solid text-sm text-yellow-light font-medium border-yellow-light transition-all duration-200 ${
                      disabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-secondary-yellow-darken hover:text-white cursor-pointer'
                    }`}>
                    {browseButtonText}
                  </label>
                  <input
                    id="file-input-upload"
                    type="file"
                    accept={acceptedFileTypes}
                    multiple={allowMultiple}
                    style={{ display: 'none' }}
                    onChange={handleAddFile}
                    disabled={disabled}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {uploadedFiles.length === 0 && (
          <p className="text-xs text-border-secondary mt-2">
            {fileTypeDescription}
          </p>
        )}

        {/* Drag indicator */}
        {isDragging && draggedFileName && (
          <div
            style={{
              position: 'fixed',
              bottom: mousePosition.y + 10,
              left: mousePosition.x + 10,
              backgroundColor: '#FEF7E8',
              border: '1px solid #FFAD00',
              color: '#131517',
              padding: '8px 12px',
              borderRadius: '50px',
              zIndex: 1000,
              pointerEvents: 'none',
              fontSize: '14px',
            }}>
            {draggedFileName}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
